import React, { useCallback, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import { Feature } from 'config/features';
import { useAtomValue } from 'jotai';

import { useFeature } from 'hooks/useFeature';

import { changedTooltipAtom } from 'data/Atom/IntegratedOvernight';

import { CommunicationContext } from 'utility/Provide/Context/PageContext';
import { dispatchStatsDEvent } from 'utility/Track/statsD';

import { PageType, VerticalType } from 'types/common';
import { SwitchData } from 'types/legacy/backendInformation';

import {
    dataMapping,
    statsDMapping,
} from '../../__helper/integratedOvernightRouteMapping';

import { redirectToVertical } from './__helper/redirectToVertical';
import ChangedInfo from './__partials/ChangedInfo';

import styles from './Toggle.module.scss';

interface ToggleInterface {
    isFewoToggleActive?: boolean;
    hasToolTip?: boolean;
    isChipStyle?: boolean;
    customClassList?: {
        ToggleWrapper?: string;
        Toggle?: string;
        Inactive?: string;
    };
}

const Toggle = ({
    hasToolTip = false,
    customClassList,
    isFewoToggleActive = false,
}: ToggleInterface) => {
    const { t } = useTranslation();
    const context = useContext(CommunicationContext);
    const backendInformationFunc = context.components.backendInformationFunc;
    const hasClickedRef = useRef(false);
    const route = backendInformationFunc.getData('route') as PageType;
    const isHotelToggleActive = useFeature(Feature.HotelHotelToggle);
    const showTooltip = useAtomValue(changedTooltipAtom);

    const onTabClick = useCallback(
        (type: VerticalType) => {
            if (!hasClickedRef.current) {
                hasClickedRef.current = true;
                const switchData: SwitchData | undefined =
                    backendInformationFunc.getData(dataMapping[type]) ??
                    undefined;

                dispatchStatsDEvent(statsDMapping[type]);
                redirectToVertical(type, route, switchData).catch(() => {
                    hasClickedRef.current = false;
                });
            }
        },
        [route, backendInformationFunc]
    );
    if (!isHotelToggleActive) {
        return null;
    }

    return (
        <nav
            className={classNames(
                styles.ToggleRow,
                customClassList?.ToggleWrapper
            )}
        >
            <div className={classNames(styles.Toggle, customClassList?.Toggle)}>
                {hasToolTip && showTooltip && <ChangedInfo />}
                {t('TRANS-package[i]', {
                    postProcess: 'interval',
                    count: 2,
                })}
            </div>
            <div
                className={classNames(
                    styles.Toggle,
                    styles.Inactive,
                    customClassList?.Toggle,
                    customClassList?.Inactive
                )}
                onClick={() => onTabClick(VerticalType.Hotel)}
            >
                {t('TRANS-hotel[i]', {
                    postProcess: 'interval',
                    count: 1,
                })}
            </div>
            {isFewoToggleActive && (
                <div
                    className={classNames(
                        styles.Toggle,
                        styles.Inactive,
                        customClassList?.Toggle,
                        customClassList?.Inactive
                    )}
                    onClick={() => onTabClick(VerticalType.HolidayHome)}
                >
                    {t('TRANS-holidayflat')}
                </div>
            )}
        </nav>
    );
};

export default Toggle;
